/**
 * Search suggestions under the header search box.
 *
 * Shopify's own search picks and orders the products; this only shows the first few under the box, with the same
 * neck-to-saying crop as the product cards. Results are listed for the selected style first (the search form's hidden
 * product type), then the other styles. Enter, or "See all results", still goes to the normal results page.
 * Nothing here changes the results page or its ranking. To switch it off, remove the two lines in layout/theme.liquid.
 */
(() => {
  const MIN_CHARS = 3;
  const MAX_SHOWN = 6;
  // zoom, and the centre of the crop (x, y as fractions of the picture); the same numbers as assets/product-card-crop.css
  const CARD_AR = 1.3;
  const CROP = { 'T-Shirt': [1.65, 0.5, 0.285], 'Long Sleeve': [1.6, 0.5, 0.275], Sweatshirt: [1.7, 0.5, 0.275], Hoodie: [1.5, 0.5, 0.29], Hat: [1.2, 0.5, 0.36], Mug: [1, 0.48, 0.47] };
  const money = (v) => '$' + String(Math.round(parseFloat(v) * 100) / 100).replace(/\.0+$/, '');
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cache = new Map();
  let counter = 0;

  function thumb(p) {
    const src = (p.featured_image && p.featured_image.url) || p.image;
    if (!src) return '';
    const u = new URL(src, location.origin);
    u.searchParams.set('width', '480');
    const [s, cx, cy] = CROP[p.type] || [1, 0.5, 0.5];
    const style = `width:${s * 100}%;transform:translate(${(0.5 / s - cx) * 100}%,${(0.5 / (s * CARD_AR) - cy) * 100}%)`;
    return `<span class="cm-suggest__thumb"><img src="${esc(u.toString())}" alt="" loading="lazy" style="${style}"></span>`;
  }

  function setup(input) {
    const form = input.closest('form');
    if (!form || form.dataset.cmSuggest) return;
    form.dataset.cmSuggest = '1';
    form.classList.add('cm-suggest-host');
    const box = document.createElement('div');
    box.className = 'cm-suggest';
    box.id = 'cm-suggest-' + ++counter;
    box.setAttribute('role', 'listbox');
    box.hidden = true;
    form.appendChild(box);
    input.setAttribute('aria-controls', box.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');
    let timer;
    let controller;
    let active = -1;

    const items = () => [...box.querySelectorAll('.cm-suggest__item, .cm-suggest__all')];
    const hide = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; };
    const mark = (i) => {
      items().forEach((el, n) => { el.classList.toggle('is-active', n === i); el.setAttribute('aria-selected', n === i ? 'true' : 'false'); });
      active = i;
      const el = items()[i];
      if (el) input.setAttribute('aria-activedescendant', el.id);
      else input.removeAttribute('aria-activedescendant');
    };

    function render(query, products) {
      const type = (form.querySelector('input[name="options[filter.p.product_type]"]') || {}).value || '';
      const own = products.filter((p) => type && p.type === type);
      const rest = products.filter((p) => !own.includes(p));
      const list = own.concat(rest).slice(0, MAX_SHOWN);
      if (!list.length) { hide(); return; }
      const seeAll = form.action + '?' + new URLSearchParams(new FormData(form)).toString();
      box.innerHTML = list.map((p, n) => {
        const title = p.title.replace(/\s+-\s+[^-]+$/, '');
        return `<a class="cm-suggest__item" role="option" id="${box.id}-o${n}" aria-selected="false" href="${esc(p.url)}">${thumb(p)}<span class="cm-suggest__text"><span class="cm-suggest__title">${esc(title)}</span><span class="cm-suggest__meta">${esc(p.type || '')} · ${money(p.price)}</span></span></a>`;
      }).join('') + `<a class="cm-suggest__all" role="option" id="${box.id}-all" aria-selected="false" href="${esc(seeAll)}">See all results for “${esc(query)}”</a>`;
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
    }

    function search() {
      const q = input.value.trim();
      if (q.length < MIN_CHARS) { hide(); return; }
      if (cache.has(q)) { render(q, cache.get(q)); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=10';
      fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((j) => {
          const products = (j.resources && j.resources.results && j.resources.results.products) || [];
          cache.set(q, products);
          if (input.value.trim() === q) render(q, products);
        })
        .catch(() => {});
    }

    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(search, 250); });
    input.addEventListener('focus', () => { if (box.innerHTML && input.value.trim().length >= MIN_CHARS) box.hidden = false; });
    input.addEventListener('keydown', (e) => {
      if (box.hidden) return;
      const n = items().length;
      if (e.key === 'ArrowDown') { e.preventDefault(); mark((active + 1) % n); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); mark((active - 1 + n) % n); }
      else if (e.key === 'Escape') { hide(); }
      else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); items()[active].click(); }
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) hide(); });
  }

  const init = () => document.querySelectorAll('form.search input[type="search"][name="q"]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
